import type { Metrics } from '../../../utils/contracts';

import { useEffect, useState } from 'react';

import { useI18n } from 'nordic/i18n';
import { Button, ButtonText } from '@andes/button';
import { Text, Title } from '@andes/typography';
import { Tooltip } from '@andes/tooltip';
import Info16 from '@andes/icons/Info16';
import Share16 from '@andes/icons/Share16';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';

import {
  type DeviceType,
  type OsNames,
  DEVICE_TYPES,
  OS_NAMES,
} from '../../utils/utils';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useWordings } from '../../hooks/useWordings';
import { usePagination } from '../../providers/Pagination/src/PaginationProvider';
import { copyToClipboard } from '../../../utils/scripts';
import { getLocalItem, setLocalItem } from '../../utils/localStorage';
import { sendMelidata } from '../../utils/sendMelidata';
import { TrackPaths } from '../../../utils/constants';
import { useAppContext } from '../../providers/AppProvider';

const KEY_TOOLTIP = 'aff-social-shared-tooltip';

export interface ProductListProps {
  isOwner: boolean;
  shareLink: string;
  deviceType: DeviceType;
  osName: OsNames;
  metrics: Metrics;
  listName: string;
}

export const ProductListComponent = ({
  isOwner,
  shareLink,
  deviceType,
  osName,
  metrics,
  listName,
}: ProductListProps) => {
  const { env } = useAppContext();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    const showTooltip = getLocalItem(KEY_TOOLTIP, true) as boolean;

    setTooltipOpen(showTooltip);
  }, []);

  const { i18n } = useI18n();
  const {
    SHARE_LIST,
    FAIL_COPY_LINK,
    SUCCESS_COPY_LINK,
    SHARE_LIST_MOBILE,
    TOOLTIP_TOTAL_PRODUCTS,
    TOOLTIP_SHARED_CONTENT,
    TOOLTIP_SHARED_TITLE,
    MY_RECOMMENDATIONS,
  } = useWordings();
  const { totalItems } = usePagination();

  const shareLinkUrl = `${shareLink}${env ? `&env=${env}` : ''}`;
  const { showSnackbar } = useSnackbar();

  const onSuccess = () => showSnackbar(SUCCESS_COPY_LINK, 'green');
  const onError = () => showSnackbar(FAIL_COPY_LINK);

  const listNameText =
    listName === 'DEFAULT'
      ? MY_RECOMMENDATIONS
      : listName || MY_RECOMMENDATIONS;
  const handleShareLink = () => {
    if (deviceType === DEVICE_TYPES.DESKTOP) {
      copyToClipboard(shareLinkUrl, onSuccess, onError);
    } else if (navigator.share && osName === OS_NAMES.IOS) {
      navigator
        .share({
          title: listNameText,
          url: shareLinkUrl,
        })
        .then(() => onSuccess())
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.error('Error sharing: ', error);
          } else {
            onError();
            console.error('Error sharing', error);
          }
        });
    } else if (window.MobileWebKit && osName === OS_NAMES.ANDROID) {
      window.MobileWebKit.actions
        .share(listNameText, shareLinkUrl)
        .then(() => onSuccess())
        .catch((error) => {
          onError();
          console.error('Error sharing', error);
        });
    } else {
      copy(shareLinkUrl);
      onSuccess();
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    const {
      owner_id: ownerId,
      total_results: totalResults,
      ...restMetrics
    } = metrics;

    sendMelidata('cleanAndSend', 'event', TrackPaths.ACTIONS, {
      ...restMetrics,
      action_type: 'click',
      action_section: 'share_list',
    });
    handleShareLink();
  };

  const onCloseTooltip = () => {
    setLocalItem(KEY_TOOLTIP, false);
    setTooltipOpen(false);
  };

  const totalProducts = totalItems > 0;

  const getButton = () => (
    <Button
      hierarchy="transparent"
      onClick={handleSubmit}
      size="medium"
      type="button"
      disabled={totalItems < 1 || !shareLink}
    >
      <Share16 />
      <ButtonText>
        {deviceType === 'mobile' ? SHARE_LIST_MOBILE : SHARE_LIST}
      </ButtonText>
    </Button>
  );

  const tooltipClass = classNames('aff-social-tooltip', {
    'aff-social-tooltip__mobile': deviceType === 'mobile',
  });

  const showTooltip = isOwner && tooltipOpen;

  return (
    <>
      <div className="container-title">
        <Title component="h1" size="m">
          {listNameText}
        </Title>
        {showTooltip ? (
          <Tooltip
            className={tooltipClass}
            actions={
              <Button
                hierarchy="transparent"
                onClick={onCloseTooltip}
                size="medium"
                type="button"
              >
                <ButtonText>{i18n.gettext('Entendido')}</ButtonText>
              </Button>
            }
            content={
              <Text
                className="aff-social-shared__tooltip"
                color="inverted"
                size="s"
              >
                {TOOLTIP_SHARED_CONTENT}
              </Text>
            }
            open={tooltipOpen}
            title={TOOLTIP_SHARED_TITLE}
            type="highlight"
            side="bottomRight"
          >
            {getButton()}
          </Tooltip>
        ) : (
          getButton()
        )}
      </div>
      {totalProducts && (
        <div className="cant-product">
          <Text
            color="secondary"
            component="span"
            className="cant-product-subtitle"
            size="s"
          >
            {i18n.ngettext(
              '{0} producto',
              '{0} productos',
              totalItems,
              totalItems,
            )}
          </Text>
          {isOwner && (
            <Tooltip content={TOOLTIP_TOTAL_PRODUCTS} side="right">
              <Info16 color="#3483FA" />
            </Tooltip>
          )}
        </div>
      )}
    </>
  );
};
